import {
  BarChart3,
  QrCode,
  BadgeDollarSign,
  Bike,
  Bot,
  Route,
  Building2,
  CalendarClock,
  Clock,
  Compass,
  CreditCard,
  Download,
  FileCode2,
  FileSpreadsheet,
  FileText,
  Gauge,
  HandCoins,
  HardDrive,
  CircleHelp,
  House,
  Images,
  Inbox,
  Landmark,
  ListChecks,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  Monitor,
  Package,
  PackageCheck,
  Palette,
  Globe,
  Percent,
  PiggyBank,
  Printer as PrinterIcon,
  ReceiptText as ReceiptTextIcon,
  Receipt,
  RefreshCcw,
  ScanBarcode,
  Settings,
  Shirt,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Store,
  Tag as TagIcon,
  Tags,
  Ticket,
  type LucideIcon,
  Users,
  UsersRound,
  Wallet,
  Warehouse,
  Wrench,
} from 'lucide-react'

import type { Permission, TenantFeature } from '@aeris/permissions'

import type { WorkspaceModule } from './workspace.ts'

/**
 * Every screen in the product, once.
 *
 * The sidebar and the Início screen both need this list, and a second copy is
 * a copy that drifts: the version that ships a new module and the version that
 * does not are indistinguishable until somebody notices a feature they cannot
 * reach from the home page.
 *
 * `description` exists only here. The sidebar has room for a label and the home
 * page does not have to guess what a label means — "Preços" is obvious to
 * whoever built it and not to somebody opening the product on their first
 * morning, which is exactly who reads the home page.
 *
 * `group` is what makes the grid scannable. The order is the order of a day's
 * work — sell, then stock what was sold, then settle up, then administer —
 * rather than alphabetical, which optimises for a list nobody is reading
 * alphabetically.
 */

/**
 * `atendimento` existe porque o módulo de WhatsApp tem DEZ telas.
 *
 * Sem ele, oito delas caíam em `administração` — que já tinha formas de
 * pagamento, terminais, canais, empresa, equipe e edge — virando quatorze
 * irmãs numa seção só; e as duas de uso diário, o inbox e os chamados, ficavam
 * separadas dessas oito, em `operação`. Não havia lugar nenhum no produto onde
 * "atendimento" fosse uma coisa só, e um administrador que quisesse configurar
 * o robô tinha de saber de cor em qual das duas seções procurar.
 *
 * Fica entre `operação` e `catálogo`: atender é trabalho de todo dia, não de
 * configuração.
 */
export type ModuleGroup =
  'operação' | 'atendimento' | 'catálogo' | 'gestão' | 'administração'

export type ModuleEntry = {
  readonly module: WorkspaceModule
  readonly label: string
  readonly Icon: LucideIcon
  /** One line, in the words of somebody who has not used the product before. */
  readonly description: string
  readonly group: ModuleGroup
  /**
   * Hides the entry when the user cannot use it.
   *
   * Hiding, not protecting: the API refuses the request independently. A menu
   * that offers a screen which then answers 403 is worse than one that does
   * not offer it.
   */
  readonly permission?: Permission
  /**
   * O módulo dentro do qual este aparece.
   *
   * Preços e Estoque são perguntas sobre um produto — quanto custa e quantos
   * tem — e ficavam ao lado de Produtos como se fossem outro assunto. Aninhar
   * é dizer no menu o que já é verdade no negócio.
   */
  readonly parent?: WorkspaceModule
  /**
   * Fora do menu, sem sair do produto.
   *
   * A rota continua existindo e funcionando; o que ela não faz é ocupar uma
   * linha do menu de quem ainda não precisa dela. Esconder uma tela pronta é
   * reversível numa linha; apagá-la não é.
   */
  readonly hidden?: boolean
  /**
   * Esta tela é ABA daquela outra (F068).
   *
   * Sai da barra lateral e **continua** no Ctrl+K e na grade do Início: a
   * pessoa que digita "custos" tem que achar Custos de atendimento, mesmo que
   * ele já não ocupe uma linha permanente do menu.
   *
   * Por isso não é `hidden`. Os três lugares que listam telas usam o filtro
   * idêntico, então marcar como escondida tiraria a tela também da busca — e
   * some do menu mais some da procura é como uma tela pronta deixa de existir.
   * Só a barra lateral olha para este campo.
   *
   * A rota da tela **não muda**: ela continua em `/atendimento-canais`, com o
   * mesmo módulo, a mesma entrada em `PAGES` e a mesma permissão. O admin
   * continua podendo negar uma aba sem negar as outras. Ver
   * `docs/features/F068-settings-tabs.md`.
   */
  readonly inTab?: WorkspaceModule
  /**
   * O nome curto, quando esta tela aparece como aba.
   *
   * "Canais de atendimento" é o nome certo numa lista onde ele convive com
   * "Canais" (marketplaces); dentro de uma página chamada *Configurações do
   * atendimento* ele vira redundante e come a largura da trilha. Sem isto,
   * vale `label`.
   */
  readonly tabLabel?: string
  /**
   * A aba em que a página de configuração abre, quando a pessoa pode abri-la.
   *
   * Sem isto, o hub caía na PRIMEIRA aba declarada — e a ordem de declaração
   * passou a decidir a porta de entrada de uma tela inteira sem que ninguém
   * escolhesse. Foi o que aconteceu na F088: `marcadores` entrou no começo da
   * lista e `/configuracoes` deixou de abrir em Formas de pagamento, em
   * silêncio, porque o e2e que cobria isso já estava vermelho por outro motivo.
   *
   * A ordem das ABAS na trilha continua sendo a de `MODULES` — este campo diz
   * só onde a página abre. Continua havendo reserva: se quem olha não pode
   * abrir a aba marcada, vale a primeira que ele pode.
   */
  readonly isTabDefault?: boolean
  /**
   * Aparece no menu se a pessoa tiver QUALQUER uma destas permissões.
   *
   * Só as duas páginas de configuração usam. Elas não têm permissão própria —
   * são um redirecionamento para a primeira aba visível —, mas mostrar
   * "Configurações do atendimento" para um caixa que não pode abrir nenhuma
   * das oito abas seria oferecer uma porta para uma sala vazia.
   *
   * Diferente de `permission`, que é uma só e vai para `PAGES`: esta é
   * derivada das abas e vale apenas para decidir a linha do menu.
   */
  readonly anyOf?: readonly Permission[]
  /**
   * A chave de conta que faz esta tela existir (F169).
   *
   * O mesmo campo de `PageEntry`, e `modules.test.tsx` compara os dois: uma
   * tela cuja chave só uma das listas conhecesse apareceria no menu e sumiria
   * no servidor, ou o contrário.
   *
   * Ausente na esmagadora maioria — tela sem chave é tela do produto, e está
   * lá para toda loja.
   */
  readonly feature?: TenantFeature
}

export const MODULES: readonly ModuleEntry[] = [
  {
    module: 'inicio',
    label: 'Início',
    Icon: House,
    description: 'O resumo do dia.',
    group: 'operação',
  },
  /*
   * O glossário do produto (F156).
   *
   * `hidden`: sai da barra lateral, mas continua no Ctrl+K e alcançável pelo
   * menu de ajuda. É referência, não trabalho do dia — e uma linha permanente
   * na barra competiria com as telas que a pessoa abre de verdade.
   *
   * Sem permissão, e não negável (`UNDENIABLE_PAGES`): quem tem menos telas é
   * quem mais precisa da explicação.
   */
  {
    module: 'ajuda',
    label: 'Ajuda',
    Icon: CircleHelp,
    description: 'O que cada situação quer dizer, e o que cada cor significa.',
    group: 'operação',
    hidden: true,
  },
  {
    module: 'pdv',
    label: 'PDV',
    Icon: ScanBarcode,
    description: 'Vender no balcão, com leitor de código de barras.',
    group: 'operação',
    /*
     * `sales.write` e não `pos.operate` (F035).
     *
     * Os dois lugares do balcão entram por esta porta: o vendedor monta o
     * orçamento e o caixa recebe. A tela se adapta; a permissão de mexer em
     * dinheiro é conferida lá dentro, e a API recusa por conta própria.
     */
    permission: 'sales.write',
  },
  {
    module: 'caixa',
    label: 'Caixa',
    Icon: Wallet,
    description: 'Abrir e fechar a gaveta, sangria e conferência.',
    group: 'operação',
    // An operator works the till, so the entry is shown on `pos.operate`. The
    // sangria and the adjustment behind it are gated on `pos.supervise`.
    permission: 'pos.operate',
  },
  {
    module: 'vendas',
    label: 'Vendas',
    Icon: Receipt,
    description: 'Todas as vendas emitidas, com o que foi cobrado.',
    group: 'operação',
    // An operator sees and writes documents but cannot cancel one, so the entry
    // is shown on `sales.read` and the cancel control is gated on
    // `sales.cancel`.
    permission: 'sales.read',
  },
  {
    module: 'trocas',
    label: 'Trocas e vales',
    Icon: RefreshCcw,
    description:
      'O que o cliente devolveu, se voltou ao estoque, e o vale que ficou para ele.',
    group: 'operação',
    // `exchange.read`: o caixa e o vendedor veem a fila e o saldo de vale; quem
    // conclui é o trocador (`exchange.manage`), e o botão é que fica atrás disso.
    permission: 'exchange.read',
  },

  {
    module: 'produtos',
    label: 'Produtos',
    Icon: Package,
    description:
      'O que a loja vende: código, marca, unidade, código de barras.',
    group: 'catálogo',
    permission: 'catalog.read',
  },
  {
    module: 'etiquetas',
    parent: 'produtos',
    label: 'Etiquetas',
    Icon: PrinterIcon,
    description:
      'A fila de etiquetas (selos, adesivos) a imprimir: código de barras, preço e nome do produto.',
    group: 'catálogo',
    // `catalog.read`: etiquetar não altera o cadastro. Quem enxerga o produto
    // pode colar o código de barras dele numa peça.
    permission: 'catalog.read',
  },
  {
    module: 'precos',
    parent: 'produtos',
    label: 'Preços',
    Icon: Tags,
    description: 'Tabelas de preço e faixas por quantidade.',
    group: 'catálogo',
    // An operator sees prices and cannot set them, so the entry is shown on
    // `pricing.read` and the controls behind it are gated on `pricing.write`.
    permission: 'pricing.read',
  },
  {
    module: 'estoque',
    parent: 'produtos',
    label: 'Estoque',
    Icon: Warehouse,
    description: 'Quanto existe, quanto está reservado, quanto dá para vender.',
    group: 'catálogo',
    permission: 'inventory.read',
  },
  {
    module: 'importar',
    label: 'Importar',
    Icon: FileSpreadsheet,
    description: 'Trazer um catálogo inteiro de uma planilha.',
    group: 'catálogo',
    // Bringing a catalogue over rewrites products, so it sits behind the same
    // permission as editing them.
    permission: 'catalog.write',
  },

  {
    module: 'importar-fotos',
    parent: 'importar',
    label: 'Importar fotos',
    Icon: Images,
    description:
      'Um .zip com as fotos, cada arquivo nomeado com o código do produto.',
    group: 'catálogo',
    // A mesma permissão de trazer o catálogo: a foto é parte do cadastro do
    // produto, e escrevê-la é escrever nele.
    permission: 'catalog.write',
  },

  {
    module: 'compras',
    label: 'Compras',
    Icon: ShoppingCart,
    description: 'Pedidos ao fornecedor e o recebimento da mercadoria.',
    group: 'gestão',
    // Shown on `purchasing.read`, which an operator holds so they can answer a
    // question at the counter. Ordering and receiving are gated inside.
    permission: 'purchasing.read',
  },
  {
    module: 'financeiro',
    label: 'Financeiro',
    Icon: Landmark,
    description: 'Contas a pagar e a receber, com baixas e vencimentos.',
    group: 'gestão',
    // `finance.read`, which an operator does not hold: what the shop owes and
    // is owed is not counter information.
    permission: 'finance.read',
  },
  {
    module: 'cobrancas',
    label: 'Cobranças',
    Icon: HandCoins,
    description:
      'Quem deve, há quanto tempo e o que foi combinado: ligar, mandar WhatsApp, receber e renegociar.',
    group: 'gestão',
    permission: 'finance.read',
  },
  {
    module: 'relatorios',
    label: 'Relatórios',
    Icon: BarChart3,
    description:
      'Quem mais compra, o que mais sai, o que mais volta em troca e quem mais vende — no período que você escolher.',
    group: 'gestão',
    // Perto de Comissões: relatório é leitura de todo dia de quem dirige a
    // loja, não configuração que se abre uma vez.
    permission: 'report.read',
  },
  {
    module: 'comissoes',
    label: 'Comissões',
    Icon: Percent,
    description:
      'A comissão de cada vendedor no mês: vendas, trocas, a %, fechar o mês e lançar o pagamento.',
    group: 'gestão',
    permission: 'commission.read',
  },
  {
    module: 'pix',
    label: 'Pix recebidos',
    Icon: QrCode,
    description:
      'Quanto entrou em Pix pelo atendimento: por dia, por vendedor e por filial — e se o caixa já viu.',
    group: 'gestão',
    permission: 'pix.charge.read',
  },
  {
    module: 'gastos',
    label: 'Folha e gastos',
    Icon: PiggyBank,
    description:
      'A folha dos funcionários no mês e o caderno de gastos da loja: marmita, bobina, salário — e quanto saiu no total.',
    group: 'gestão',
    // O caixa vê e preenche (F146); definir salário é `expense.manage`, dentro.
    permission: 'expense.read',
  },
  /*
   * O ponto dos funcionários (F339), atrás da chave `ponto`.
   *
   * `feature` aqui e em `PAGES`; `modules.test.tsx` compara os dois.
   */
  {
    module: 'ponto',
    label: 'Ponto',
    Icon: Clock,
    description:
      'Quem está na loja agora, as batidas do dia com foto, o código de cada funcionário e os aparelhos que batem o ponto.',
    group: 'gestão',
    permission: 'timeclock.read',
    feature: 'ponto',
  },
  {
    module: 'pessoas',
    // "Clientes", porque é como a loja chama. Fornecedores e transportadoras
    // moram na mesma tela e no mesmo cadastro — a pessoa é uma só e os papéis
    // é que mudam —, mas quem abre este menu está procurando um cliente.
    label: 'Clientes',
    Icon: Users,
    description: 'Clientes, fornecedores e o crédito de cada um.',
    group: 'gestão',
    permission: 'party.read',
  },
  {
    module: 'orcamentos',
    label: 'Orçamentos',
    Icon: FileText,
    description: 'O que foi prometido e ainda não foi retirado.',
    group: 'operação',
    permission: 'sales.read',
  },
  {
    module: 'atendimento',
    label: 'Atendimento',
    Icon: MessagesSquare,
    description:
      'As conversas de WhatsApp da loja: quem escreveu, o que falta responder e a janela de 24 horas de cada uma.',
    group: 'atendimento',
    // A mesma permissão que a API exige para ler o inbox — quem responde no
    // WhatsApp é o balcão (F046: owner, admin, manager, cashier, salesperson).
    permission: 'communication.conversation.read',
  },
  {
    module: 'atendimento-painel',
    label: 'Painel do atendimento',
    Icon: Gauge,
    description:
      'Quantas conversas cada atendente tem, quantas esperam resposta e há quanto tempo, e quanto cada um demora para responder. Passe conversas de quem está cheio para quem está livre.',
    group: 'atendimento',
    // Quem supervisiona: a mesma permissão que deixa tomar uma conversa de
    // outra pessoa (dono, admin e gerente).
    permission: 'communication.queue.write',
  },
  {
    module: 'chamados',
    label: 'Chamados',
    Icon: Ticket,
    description:
      'O atendimento que não acaba quando a conversa fecha: protocolo, situação, prioridade e responsável.',
    group: 'atendimento',
    // Mesmos cinco papéis que atendem no WhatsApp — inclusive cashier e
    // salesperson, que é quem abre chamado no balcão (F051).
    permission: 'communication.ticket.read',
  },
  /*
   * Daqui até Custos é a CONFIGURAÇÃO do atendimento (F068).
   *
   * A F046 tirou estas oito de `administração` e deu a elas um grupo próprio,
   * porque em `administração` ninguém as achava. Deu certo, e produziu o
   * problema seguinte: o grupo virou DEZ linhas, das quais oito são telas que
   * se abre uma vez e não se volta. O dono descreveu assim, olhando o produto
   * no ar: *"a sidebar tá com mt página desnecessária q poderia ser 1, tá
   * estranho"*.
   *
   * Agora são abas de `atendimento-config` — uma linha de menu. `inTab` só
   * tira da barra lateral: cada uma continua com a própria rota, a própria
   * entrada em `PAGES` e a própria permissão, e continua no Ctrl+K.
   */
  {
    module: 'atendimento-config',
    label: 'Configurações do atendimento',
    Icon: SlidersHorizontal,
    description:
      'Tudo o que se configura uma vez: o número conectado, as filas, os modelos aprovados, o robô, as regras de follow-up, as campanhas e o custo das mensagens.',
    group: 'atendimento',
    /*
     * Sem permissão própria: esta rota não desenha nada, ela manda para a
     * primeira aba que a pessoa pode abrir. Quem decide o que ela vê são as
     * oito permissões abaixo, uma por aba — `anyOf` só evita oferecer a porta
     * a quem não pode abrir nenhuma.
     */
    anyOf: [
      'communication.channel.read',
      'communication.queue.read',
      'communication.template.read',
      'communication.bot.read',
      'communication.followup.read',
      'communication.campaign.read',
      'communication.cost.read',
    ],
  },
  {
    module: 'atendimento-guia',
    inTab: 'atendimento-config',
    tabLabel: 'Guia',
    label: 'Guia de configuração',
    Icon: Compass,
    description:
      'Os dez passos do atendimento, do número conectado até a primeira campanha — cada um confirmado sozinho pelo que já existe no banco.',
    group: 'atendimento',
    // A mesma permissão de Canais de atendimento: o primeiro passo do guia
    // é conectar um canal.
    permission: 'communication.channel.read',
  },
  {
    module: 'atendimento-canais',
    inTab: 'atendimento-config',
    tabLabel: 'Canais',
    label: 'Canais de atendimento',
    Icon: MessageCircle,
    description:
      'A conta de WhatsApp que fala com o gateway (OpenBSP): número, credenciais e a URL do webhook.',
    group: 'atendimento',
    // Separada de `channel.read` (F030/F032, marketplaces): uma conta de
    // canal de atendimento carrega uma credencial diferente e decide de que
    // número a loja atende no WhatsApp — outro assunto com a mesma palavra.
    permission: 'communication.channel.read',
  },
  {
    module: 'atendimento-grupos',
    inTab: 'atendimento-config',
    tabLabel: 'Grupos',
    label: 'Grupos do WhatsApp',
    Icon: UsersRound,
    description:
      'Quais grupos do WhatsApp entram no Atendimento, e de qual loja cliente é cada um (F268).',
    group: 'atendimento',
    permission: 'communication.group.manage',
  },
  {
    module: 'atendimento-filas',
    inTab: 'atendimento-config',
    tabLabel: 'Filas',
    label: 'Filas de atendimento',
    Icon: ListChecks,
    description:
      'Quem atende, e quando: cada fila tem seus membros e um horário opcional. O robô que respeita esse horário chega depois (F052).',
    group: 'atendimento',
    // A mesma permissão que a API exige para ler as filas — administrar
    // membros e horário é `.write`, restrito a owner/admin/manager dentro da
    // tela.
    permission: 'communication.queue.read',
  },
  {
    module: 'atendimento-modelos',
    inTab: 'atendimento-config',
    label: 'Modelos',
    Icon: FileText,
    description:
      'Modelos aprovados pela Meta: categoria (decide o custo), situação e o motivo da recusa quando houver. Fora da janela de 24h, é a única coisa que o Aeris pode enviar.',
    group: 'atendimento',
    // A mesma permissão que a API exige para ler os modelos — criar e
    // submeter à Meta é `.manage`, restrito a owner/admin/manager dentro da
    // tela.
    permission: 'communication.template.read',
  },
  {
    module: 'atendimento-robo',
    inTab: 'atendimento-config',
    tabLabel: 'Robô',
    label: 'Robô de atendimento',
    Icon: Bot,
    description:
      'O robô que responde primeiro dentro da janela de 24h: provedores, prompt, horário humano, palavras de handoff e o teto de custo por conversa. Mostra o gasto do período.',
    group: 'atendimento',
    // A mesma lista de `communication.channel.read` (owner/admin/manager) —
    // não é trabalho de balcão, é a configuração de como a loja fala com o
    // cliente e quanto isso custa.
    permission: 'communication.bot.read',
  },
  {
    module: 'atendimento-robo-preco',
    inTab: 'atendimento-config',
    tabLabel: 'Robô de preço',
    label: 'Robô de preço',
    Icon: Tags,
    description:
      'O número que responde sozinho a tabela de preço: "tela a03" recebe as telas do A03 com estoque e preço, sem IA. Seções, filial do estoque, peças fora do robô e o histórico do que ele respondeu.',
    group: 'atendimento',
    // O mesmo tipo de decisão do robô de IA: o que sai sozinho em nome da loja.
    permission: 'communication.bot.read',
  },
  {
    module: 'atendimento-pos-venda',
    inTab: 'atendimento-config',
    tabLabel: 'Pós-venda',
    label: 'Pós-venda',
    Icon: PackageCheck,
    description:
      'O que a venda manda ao cliente pelo número de quem vendeu: "seu pedido saiu com o motoboy" e, horas depois, "deu certo a instalação?". Quem responde que deu certo recebe a oferta; quem teve problema vai para a vendedora.',
    group: 'atendimento',
    // A mesma permissão do follow-up (F053): mensagem que sai sozinha depois
    // da venda.
    permission: 'communication.followup.read',
  },
  {
    module: 'crm-regras',
    inTab: 'atendimento-config',
    tabLabel: 'Follow-up',
    label: 'Regras de follow-up',
    Icon: CalendarClock,
    description:
      'Comprou ar-condicionado? Lembrete de manutenção em 6 meses. Uma regra por produto ou marca, um modelo aprovado, e cancela sozinha se o cliente comprar de novo antes do vencimento.',
    group: 'atendimento',
    // A mesma lista de `communication.bot.read` — quem mexe aqui decide o
    // que a loja promete ao cliente meses depois de uma venda.
    permission: 'communication.followup.read',
  },
  {
    module: 'crm-campanhas',
    inTab: 'atendimento-config',
    label: 'Campanhas',
    Icon: Megaphone,
    description:
      'Pegar todos os clientes que compraram um produto (ou marca) e avisar de uma vez — reajuste, lançamento, o que for. Pré-visualiza quantas pessoas o segmento pega, com quantos já pediram para sair, antes de disparar.',
    group: 'atendimento',
    permission: 'communication.campaign.read',
  },
  {
    module: 'atendimento-custos',
    inTab: 'atendimento-config',
    tabLabel: 'Custos',
    label: 'Custos de atendimento',
    Icon: BadgeDollarSign,
    description:
      'Quanto a loja gastou em mensagens, por categoria e por período — comparado com o que a Meta relatar, quando houver.',
    group: 'atendimento',
    permission: 'communication.cost.read',
  },
  {
    module: 'entregas',
    label: 'Acerto do motoboy',
    Icon: Bike,
    description: 'O que cada entregador levou e ainda não prestou contas.',
    group: 'operação',
    permission: 'finance.read',
  },
  /*
   * As rotas de entrega (F265–F267), atrás da chave `entregas-rota`.
   */
  {
    module: 'rotas',
    label: 'Rotas de entrega',
    Icon: Route,
    description:
      'O cliente no mapa, a saída da loja e a rota de cada motoboy e da entrega a pé.',
    group: 'operação',
    permission: 'delivery.route.read',
    feature: 'entregas-rota',
  },
  /*
   * A fila de produção de uniformes (F170), atrás da chave `uniformes`.
   *
   * `feature` aqui e em `PAGES`; `modules.test.tsx` compara os dois.
   */
  {
    module: 'producao',
    label: 'Produção',
    Icon: Shirt,
    description:
      'A fila dos pedidos em produção: a grade de tamanhos, a etapa de cada um e em qual impressora saiu.',
    group: 'operação',
    permission: 'uniforms.read',
    feature: 'uniformes',
  },
  {
    module: 'importar-clientes',
    // As três importações num lugar só (F132): antes duas moravam em gestão e
    // uma em catálogo, e quem procurava "importar" achava uma das três.
    parent: 'importar',
    label: 'Importar clientes',
    Icon: Users,
    description: 'Trazer a carteira de clientes de uma planilha.',
    group: 'catálogo',
    // `party.write`, não `party.create`: o operador de balcão tem `create` de
    // propósito, para cadastrar quem está na frente dele — e isso não é o mesmo
    // que trazer três mil cadastros de uma vez e bloquear alguns.
    permission: 'party.write',
  },
  {
    module: 'importar-nfe',
    parent: 'importar',
    label: 'Importar NF-e',
    Icon: FileCode2,
    description: 'Ler o XML do fornecedor e virar uma compra.',
    group: 'catálogo',
    // `purchasing.write`, not `read`: the screen exists to create a purchase,
    // and showing it to somebody who cannot would be an invitation to a 403.
    permission: 'purchasing.write',
  },

  /*
   * A administração inteira numa página só (F068).
   *
   * Sete telas que se abre uma vez por loja — a forma de pagamento que ela
   * aceita, os caixas físicos, os marketplaces, o CNPJ, quem tem acesso, as
   * máquinas que rodam offline e os marcadores (esta desde a F088). Mesma
   * queixa e mesmo remédio do atendimento:
   * viram abas, e cada uma continua com a própria rota, a própria entrada em
   * `PAGES` e a própria permissão.
   */
  {
    module: 'configuracoes',
    label: 'Configurações da loja',
    Icon: Settings,
    description:
      'O que se ajusta uma vez: formas de pagamento, terminais do balcão, marketplaces, o cadastro da empresa, quem tem acesso e as máquinas que rodam o Aeris na loja.',
    group: 'administração',
    // Sem permissão própria — ver `atendimento-config`. Uma permissão por aba,
    // e a lista tem que crescer junto: `pos.operate` está aí porque Terminais é
    // a única que um operador de balcão enxerga, e `tag.read` porque a F088
    // acrescentou Marcadores e esqueceu esta linha — quem só tinha `tag.read`
    // ficava sem a porta no menu para uma aba que respondia pela URL.
    anyOf: [
      'payment.read',
      'pos.operate',
      'channel.read',
      'organization.write',
      'identity.user.read',
      'edge.manage',
      'tag.read',
      // F158: Marcas é aba daqui, e sem esta linha quem administra o catálogo
      // ficava sem a porta no menu para uma aba que respondia pela URL — o
      // mesmo esquecimento que a F088 cometeu com `tag.read`.
      'catalog.write',
      // F170: Impressoras da produção é aba daqui — o mesmo esquecimento,
      // evitado antes de acontecer.
      'uniforms.manage',
    ],
  },
  {
    module: 'marcadores',
    inTab: 'configuracoes',
    tabLabel: 'Marcadores',
    label: 'Marcadores',
    Icon: TagIcon,
    description:
      'Os rótulos que a loja cola em clientes e em conversas — "Atacado", "Aguardando pagamento".',
    group: 'administração',
    // `tag.read` e não `tag.manage`: quem só cola marcadores precisa ver a
    // lista para saber o que existe, e o botão de criar é que fica atrás da
    // permissão de administrar.
    permission: 'tag.read',
  },
  /*
   * A marca da loja (F169), atrás da chave `marca-propria`.
   *
   * `feature` aqui e em `PAGES`, e `modules.test.tsx` compara os dois: numa
   * lista só, a tela apareceria no menu e sumiria no servidor — ou o
   * contrário, que é pior.
   */
  {
    module: 'aparencia',
    inTab: 'configuracoes',
    tabLabel: 'Aparência',
    label: 'Aparência',
    Icon: Palette,
    description: 'A logo e a cor desta loja, no lugar da marca Aeris.',
    group: 'administração',
    permission: 'organization.write',
    feature: 'marca-propria',
  },
  /*
   * A loja online (F221), atrás da chave `loja-online`: o endereço que o site
   * de atacado chama, a filial que ela vende e quem está com ela aberta.
   */
  {
    module: 'loja-online',
    inTab: 'configuracoes',
    tabLabel: 'Loja online',
    label: 'Loja online',
    Icon: Globe,
    description:
      'O site de atacado lê o catálogo daqui, e o lojista entra com o número do cadastro.',
    group: 'administração',
    permission: 'storefront.manage',
    feature: 'loja-online',
  },
  /*
   * O assistente (F253–F255), atrás da chave `assistente`: as conexões do
   * Claude de todos e, na F254, o WhatsApp do assistente.
   */
  {
    module: 'assistente',
    inTab: 'configuracoes',
    tabLabel: 'Assistente',
    label: 'Assistente',
    Icon: Bot,
    description:
      'Quem pergunta os números da loja pelo Claude e pelo WhatsApp do assistente.',
    group: 'administração',
    permission: 'assistant.manage',
    feature: 'assistente',
  },
  /*
   * As impressoras da produção (F170), atrás da chave `uniformes`: sem a fila,
   * o cadastro não tem para que existir.
   */
  {
    module: 'impressoras',
    inTab: 'configuracoes',
    tabLabel: 'Impressoras',
    label: 'Impressoras da produção',
    Icon: PrinterIcon,
    description:
      'As máquinas da produção — Amarela 1, Branca 2 — que a fila marca em cada pedido.',
    group: 'administração',
    permission: 'uniforms.manage',
    feature: 'uniformes',
  },
  /*
   * A equipe da produção (F171): quem atende, quem desenha, quem imprime. A
   * função decide em que parte do pedido cada um mexe.
   */
  {
    module: 'equipe-producao',
    inTab: 'configuracoes',
    tabLabel: 'Equipe da produção',
    label: 'Equipe da produção',
    Icon: UsersRound,
    description:
      'Quem é atendente, designer e operador na produção — o que cada um mexe no pedido e vê em "Minha fila".',
    group: 'administração',
    permission: 'uniforms.manage',
    feature: 'uniformes',
  },
  {
    module: 'marcas',
    inTab: 'configuracoes',
    tabLabel: 'Marcas',
    label: 'Marcas',
    Icon: Tags,
    description:
      'O vocabulário de marcas do catálogo — o cadastro de produto escolhe daqui em vez de digitar.',
    group: 'administração',
    /*
     * `catalog.write` e não `catalog.read`: a tela existe para ADMINISTRAR o
     * vocabulário, e mostrá-la a quem só consulta produto seria a mesma
     * armadilha de `importar-nfe` logo acima — uma porta que leva a um 403.
     *
     * Quem só cadastra produto continua vendo as marcas onde importa: no
     * seletor do formulário, que lê pela mesma `catalog.read` que ele já tem.
     */
    permission: 'catalog.write',
  },
  {
    module: 'modelo-de-etiqueta',
    inTab: 'configuracoes',
    // O nome da aba é o título dela (F132), e não um apelido.
    tabLabel: 'Modelos de etiqueta',
    label: 'Modelo de etiqueta',
    Icon: PrinterIcon,
    description:
      'O tamanho do adesivo na impressora deste computador e o que sai escrito nele.',
    group: 'administração',
    // `catalog.read` porque o que se configura aqui é como um PRODUTO aparece
    // no papel — quem não enxerga o catálogo não tem o que etiquetar. E é do
    // computador, não da loja: o molde mora no navegador desta máquina.
    permission: 'catalog.read',
  },
  {
    module: 'modelo-de-nota',
    inTab: 'configuracoes',
    tabLabel: 'Modelo da nota',
    label: 'Modelo da nota',
    Icon: ReceiptTextIcon,
    description:
      'O que sai impresso na bobina: cabeçalho da loja, itens e o recado no rodapé de cada papel.',
    group: 'administração',
    // `organization.write` porque o que se configura aqui é a LOJA — CNPJ,
    // endereço e recado ao cliente —, a mesma matéria da aba Empresa. Ler o
    // modelo é `organization.read`, que todo mundo que imprime já tem.
    permission: 'organization.write',
  },
  {
    module: 'formas-de-pagamento',
    inTab: 'configuracoes',
    isTabDefault: true,
    tabLabel: 'Pagamento',
    label: 'Formas de pagamento',
    // Not the wallet: the till already owns that, and two entries sharing an
    // icon is two entries a person has to read to tell apart.
    Icon: CreditCard,
    description: 'O que a loja aceita, em que ordem e com qual taxa.',
    group: 'administração',
    // Shown on `payment.read` so an operator can see what the shop accepts;
    // the controls behind it are gated on `payment.method.manage`.
    permission: 'payment.read',
  },
  {
    module: 'terminais',
    inTab: 'configuracoes',
    Icon: Monitor,
    // "Terminais" rather than "Caixas cadastrados": two entries whose names
    // differ only by a suffix are two entries a person has to read to tell
    // apart, and /caixa is already called Caixa.
    label: 'Terminais',
    description:
      'Os caixas físicos da filial. O operador escolhe um ao abrir o turno.',
    group: 'administração',
    // Read on `pos.operate` so an operator can see which till is which; the
    // controls are gated on `pos.supervise` inside.
    permission: 'pos.operate',
  },
  {
    module: 'aplicacoes',
    hidden: true,
    label: 'Aplicações',
    Icon: Wrench,
    description:
      'Em que uma peça serve: marca e modelo, para a busca do balcão responder "esta tela serve no A20?".',
    group: 'catálogo',
    // Read on catalog.read: knowing what fits what is the counter's question,
    // and an operator needs it. Writing is gated inside on catalog.write.
    permission: 'catalog.read',
  },
  {
    module: 'pedidos-loja',
    label: 'Pedidos da loja',
    Icon: ShoppingBag,
    description:
      'O que o cliente pediu na loja online: a vendedora confere o estoque, aprova (vira pedido para separar) ou recusa com o motivo. O cliente acompanha no site e é avisado no WhatsApp.',
    group: 'operação',
    permission: 'sales.read',
    feature: 'loja-online',
  },
  {
    module: 'pedidos-canal',
    label: 'Pedidos dos canais',
    Icon: Inbox,
    description:
      'O que os marketplaces venderam, o que já virou venda, e o que está esperando alguém aceitar.',
    group: 'operação',
    permission: 'channel.read',
  },
  {
    module: 'canais',
    // "Marketplaces" na trilha (F132): havia um "Canais" em cada página de
    // configuração, e os dois são coisas diferentes.
    tabLabel: 'Marketplaces',
    inTab: 'configuracoes',
    label: 'Canais',
    Icon: Store,
    description:
      'Marketplaces e loja online: qual produto é qual anúncio, e quanto do estoque cada canal pode prometer.',
    group: 'administração',
    // Read is its own permission because the mapping is the interesting half:
    // repointing an advert at another product makes the wrong thing ship.
    permission: 'channel.read',
  },
  {
    module: 'empresa',
    inTab: 'configuracoes',
    label: 'Empresa',
    Icon: Building2,
    description: 'CNPJ, endereço, regime tributário e filiais.',
    group: 'administração',
    /*
     * `organization.write` e não `organization.read` (F035).
     *
     * O cadastro da empresa não é assunto de quem está no balcão: o CNPJ, o
     * regime tributário e o momento em que o estoque sai decidem como cada
     * documento é emitido. Quem trabalha ali continua com `organization.read`
     * — precisa saber em que loja está — e não vê a tela de configurá-la.
     */
    permission: 'organization.write',
  },
  {
    module: 'equipe',
    inTab: 'configuracoes',
    label: 'Equipe',
    Icon: Users,
    description:
      'Funcionários, vendedores e caixas: quem tem acesso, com qual papel e quais telas.',
    group: 'administração',
    permission: 'identity.user.read',
  },
  {
    module: 'edge',
    inTab: 'configuracoes',
    tabLabel: 'Máquinas da loja',
    // "Máquinas da loja", e não "Credenciais do Edge": quem abre esta tela
    // está pensando no PC do balcão que parou de mandar as vendas, não num
    // conceito de autenticação.
    label: 'Máquinas da loja',
    Icon: HardDrive,
    description:
      'Os computadores que rodam o Aeris na loja e continuam vendendo sem internet. Mostra quando cada um falou com a nuvem pela última vez.',
    group: 'administração',
    permission: 'edge.manage',
  },
  {
    module: 'meu-assistente',
    label: 'Meu assistente',
    Icon: Sparkles,
    description:
      'Pergunte os números da loja pelo Claude (e, em breve, pelo WhatsApp), com o seu acesso.',
    group: 'administração',
    permission: 'assistant.use',
    feature: 'assistente',
  },
  {
    module: 'baixar',
    // Fora do menu lateral, dentro do produto: o caminho até ela é o menu da
    // conta, que é onde alguém procura "instalar no meu computador". Uma linha
    // permanente na navegação para uma coisa que se faz uma vez por máquina é
    // uma linha gasta.
    hidden: true,
    label: 'Aeris no computador',
    Icon: Download,
    description:
      'Instalar o Aeris nos computadores da loja, para continuar vendendo quando a internet cair.',
    group: 'administração',
    // Sem permissão: quem tem conta pode instalar o produto na própria máquina,
    // e exigir um papel para isso só produziria um pedido ao administrador.
  },
]

export const MODULE_GROUPS: readonly ModuleGroup[] = [
  'operação',
  'atendimento',
  'catálogo',
  'gestão',
  'administração',
]

/**
 * O acesso rápido do balcão.
 *
 * O menu não é um índice do produto — são as quatro coisas que a pessoa faz o
 * dia inteiro, grandes o bastante para acertar sem procurar. É a forma do
 * sistema que eles já usam, e foi pedido nesses termos: *"bem básico"*.
 *
 * O resto continua tudo lá embaixo, em seções que abrem, e no Ctrl+K — um
 * produto que oferece menos telas não é um produto mais simples, é um produto
 * onde a tela existe e ninguém acha.
 */
export const COUNTER_MODULES: readonly WorkspaceModule[] = [
  'pdv',
  'orcamentos',
  'pessoas',
  'produtos',
]
